/**
 * Auth Context & Provider
 *
 * React Context for authentication state management.
 * Uses TanStack Query under the hood for efficient state caching.
 *
 * @layer features
 * @slice auth
 * @segment model
 * @example
 * // In root layout or main.tsx
 * <QueryClientProvider client={queryClient}>
 *   <AuthProvider>
 *     <App />
 *   </AuthProvider>
 * </QueryClientProvider>
 *
 * @see useAuth hook for consuming auth state
 * @added DOC-0073 (2025-12-19)
 * @updated DOC-0094 (2025-12-26) - Migrated to FSD features/auth
 */

import { createContext, useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { authMeQueryOptions, useLogoutMutation } from '../api/queries';
import type { SessionContextValue } from '@entities/session';
import { setRequesterExportProfile } from '@shared/lib/requester-export-references';
import { useUserProfileContext, useUserProfileIdentity } from '@entities/user-profile';

/**
 * Auth context
 *
 * Default value is null - consumers must be wrapped in AuthProvider.
 * Use useAuth() hook for type-safe access.
 */
export const AuthContext = createContext<SessionContextValue | null>(null);

/**
 * AuthProvider props
 */
interface AuthProviderProps {
  /** Child components that will have access to auth context */
  children: ReactNode;
}

/**
 * Auth Provider Component
 *
 * Wraps the application and provides authentication state to all children.
 * Must be placed inside QueryClientProvider.
 *
 * Features:
 * - Fetches current user on mount
 * - Provides user data, loading state, and auth status
 * - Exposes logout function
 * - Caches auth state with TanStack Query
 *
 * @example
 * // In your app root
 * <AuthProvider>
 *   <YourApp />
 * </AuthProvider>
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const logoutMutation = useLogoutMutation();

  // Fetch current user using TanStack Query
  const { data: session, isLoading, error, refetch } = useQuery(authMeQueryOptions());
  const user = session?.user ?? null;
  // Failed permission refresh must never preserve previously granted UI capabilities.
  const permissions = error === null ? (session?.permissions ?? null) : null;

  // Warm the small, account-scoped user context as soon as authentication resolves. Feature pages
  // reuse this TanStack Query entry and still re-authorize every referenced backend resource.
  useUserProfileContext(user?.id ?? '');
  const profileIdentity = useUserProfileIdentity(user?.id ?? '');
  const previousAccount = useRef<string | null | undefined>(undefined);
  const requesterProfile = profileIdentity.isError
    ? undefined
    : profileIdentity.data?.data.profileId;
  useEffect(() => {
    if (isLoading) return;
    if (previousAccount.current !== undefined && previousAccount.current !== (user?.id ?? null)) {
      setRequesterExportProfile(null);
    }
    previousAccount.current = user?.id ?? null;
    if (!user || profileIdentity.isError) setRequesterExportProfile(null);
    else if (requesterProfile) setRequesterExportProfile(requesterProfile);
  }, [isLoading, user, requesterProfile, profileIdentity.isError]);

  /**
   * Logout handler
   * Calls the logout mutation which clears session and redirects
   */
  const logout = useCallback(async () => {
    await logoutMutation.mutateAsync();
  }, [logoutMutation]);

  /**
   * Refetch handler
   * Manually refresh auth state (e.g., after role change)
   */
  const handleRefetch = useCallback(async () => {
    await refetch();
  }, [refetch]);

  /**
   * Memoized context value
   * Prevents unnecessary re-renders of consumers
   */
  const value = useMemo<SessionContextValue>(
    () => ({
      user: user ?? null,
      permissions,
      isAuthenticated: !!user,
      isLoading,
      error: error instanceof Error ? error : null,
      logout,
      refetch: handleRefetch,
    }),
    [user, permissions, isLoading, error, logout, handleRefetch]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
